/**
 * 项目权限检查 Hook
 * 提供统一的权限检查接口，自动使用缓存
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useProjectPermissions } from '@/contexts/ProjectPermissionsContext';
import { createLogger } from '@/utils/logger';

const logger = createLogger('useProjectPermission');

interface UseProjectPermissionOptions {
  projectId: string | null | undefined;
  permissionCodes: string[];
  enabled?: boolean;
}

interface UseProjectPermissionResult {
  permissions: Record<string, boolean>;
  loading: boolean;
  error: Error | null;
}

function normalizeStringList(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort();
}

function useStableNormalizedStringList(values: string[]): string[] {
  const normalized = useMemo(() => normalizeStringList(values), [values]);
  const key = normalized.join('\u0001');
  const stableRef = useRef<{ key: string; value: string[] }>({
    key: '',
    value: [],
  });

  if (stableRef.current.key !== key) {
    stableRef.current = { key, value: normalized };
  }

  return stableRef.current.value;
}

function arePermissionMapsEqual(
  a: Record<string, boolean>,
  b: Record<string, boolean>
): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}

function areBatchPermissionMapsEqual(
  a: Record<string, Record<string, boolean>>,
  b: Record<string, Record<string, boolean>>
): boolean {
  const aProjectIds = Object.keys(a);
  const bProjectIds = Object.keys(b);
  if (aProjectIds.length !== bProjectIds.length) return false;
  return aProjectIds.every((projectId) =>
    arePermissionMapsEqual(a[projectId] || {}, b[projectId] || {})
  );
}

/**
 * 使用项目权限 Hook
 *
 * @example
 * const { permissions, loading } = useProjectPermission({
 *   projectId: 'xxx',
 *   permissionCodes: ['task:create', 'task:delete'],
 * });
 *
 * if (permissions['task:create']) {
 *   // 显示创建按钮
 * }
 */
export function useProjectPermission({
  projectId,
  permissionCodes,
  enabled = true,
}: UseProjectPermissionOptions): UseProjectPermissionResult {
  const { checkPermissions } = useProjectPermissions();
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);
  const requestSeqRef = useRef(0);

  const normalizedPermissionCodes = useStableNormalizedStringList(permissionCodes);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    async function fetchPermissions() {
      const requestSeq = requestSeqRef.current + 1;
      requestSeqRef.current = requestSeq;

      if (!enabled || !projectId || normalizedPermissionCodes.length === 0) {
        setPermissions((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        setLoading(false);
        setError(null);
        return;
      }

      setLoading((prev) => (prev ? prev : true));
      setError(null);

      try {
        const result = await checkPermissions(projectId, normalizedPermissionCodes);

        if (isMountedRef.current && requestSeqRef.current === requestSeq) {
          setPermissions((prev) =>
            arePermissionMapsEqual(prev, result) ? prev : result
          );
          setLoading(false);
        }
      } catch (err) {
        logger.error('检查项目权限失败', { error: err });
        if (isMountedRef.current && requestSeqRef.current === requestSeq) {
          setError(err as Error);
          setLoading(false);
          // 权限检查失败时，默认所有权限为 false
          const defaultPermissions: Record<string, boolean> = {};
          normalizedPermissionCodes.forEach(code => {
            defaultPermissions[code] = false;
          });
          setPermissions((prev) =>
            arePermissionMapsEqual(prev, defaultPermissions)
              ? prev
              : defaultPermissions
          );
        }
      }
    }

    fetchPermissions();
  }, [projectId, enabled, normalizedPermissionCodes, checkPermissions]);

  return { permissions, loading, error };
}

/**
 * 批量权限检查结果类型
 */
export interface BatchPermissionResult {
  [key: string]: boolean;
}

/**
 * 使用批量项目权限 Hook（用于多个项目的权限检查）
 *
 * @example
 * const { permissions, loading } = useBatchProjectPermissions({
 *   projectIds: ['project1', 'project2'],
 *   permissionCodes: ['task:view_all', 'task:view_own'],
 * });
 *
 * if (permissions['project1']?.['task:view_all']) {
 *   // 显示项目1的任务
 * }
 */
export function useBatchProjectPermissions({
  projectIds,
  permissionCodes,
  enabled = true,
}: {
  projectIds: string[];
  permissionCodes: string[];
  enabled?: boolean;
}): {
  permissions: Record<string, Record<string, boolean>>;
  loading: boolean;
  error: Error | null;
} {
  const { checkPermissionsBatch } = useProjectPermissions();
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);
  const requestSeqRef = useRef(0);

  const normalizedProjectIds = useStableNormalizedStringList(projectIds);
  const normalizedPermissionCodes = useStableNormalizedStringList(permissionCodes);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    async function fetchBatchPermissions() {
      const requestSeq = requestSeqRef.current + 1;
      requestSeqRef.current = requestSeq;

      if (
        !enabled ||
        normalizedProjectIds.length === 0 ||
        normalizedPermissionCodes.length === 0
      ) {
        setPermissions((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        setLoading(false);
        setError(null);
        return;
      }

      setLoading((prev) => (prev ? prev : true));
      setError(null);

      try {
        const permissionsMap = await checkPermissionsBatch(
          normalizedProjectIds,
          normalizedPermissionCodes
        );

        if (isMountedRef.current && requestSeqRef.current === requestSeq) {
          setPermissions((prev) =>
            areBatchPermissionMapsEqual(prev, permissionsMap)
              ? prev
              : permissionsMap
          );
          setLoading(false);
        }
      } catch (err) {
        logger.error('批量检查项目权限失败', { error: err });
        if (isMountedRef.current && requestSeqRef.current === requestSeq) {
          setError(err as Error);
          setLoading(false);
          setPermissions((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        }
      }
    }

    fetchBatchPermissions();
  }, [normalizedProjectIds, normalizedPermissionCodes, enabled, checkPermissionsBatch]);

  return { permissions, loading, error };
}
